import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import IconAddLink from '../../components/icons/IconAddLink';
import { collectTopicsUnder } from '../courses/preview/TopicPreviewPanel';
import { resolveTopicCourseId } from '../courses/courseLevels';
import { getLessonTopics, createLessonTopic, deleteLessonTopic } from './workspace/lessonTopicsApi';
import language from '../../variables/language';
import '../../css/lesson-topic-link.css';

// A linked topic's pill is a real link into that student's workspace,
// scrolled straight to the topic (StudentWorkspaceView.jsx reads a
// `?topic=` param on load - see its own jumpToTopic effect). A Topic link
// itself has no student field (LessonTopic only knows lesson+topic), so
// with more than one participant the pill can't know by itself whose
// workspace to open - clicking it fetches each participant's own courses
// on demand and shows only the ones who actually have this topic's course,
// per explicit rule: a participant added to the lesson later who doesn't
// share that course must never appear as an option.
//
// The popup itself only ever appears when that filtering leaves a genuine
// choice - one raw participant skips the fetch entirely, and even with
// several raw participants, if only one of them turns out to actually have
// this course, it goes straight there too instead of showing a
// one-option "choice."
// Flyouts default to opening upward (bottom: calc(100% + 8px) - see
// lesson-topic-link.css) because their usual trigger sits low in a panel
// (e.g. TopicLessonFlyout.jsx's per-topic eyebrow row). LessonDetailModal
// puts this one right after the title, near the very top of the modal -
// opening upward from there ran the flyout straight off the top of the
// viewport. Measured on open rather than hardcoded, so it self-corrects
// wherever the trigger actually ends up.
const FLYOUT_ESTIMATED_HEIGHT = 260;

function TopicPillLink({ lt, participants, onClose }) {
   const [open, setOpen] = useState(false);
   const [openDown, setOpenDown] = useState(false);
   const [candidates, setCandidates] = useState(null);
   const wrapRef = useRef(null);
   const navigate = useNavigate();
   const lang = language[language.lang];
   const courseId = resolveTopicCourseId(lt.topic);

   useEffect(() => {
      if (open && wrapRef.current) {
         setOpenDown(wrapRef.current.getBoundingClientRect().top < FLYOUT_ESTIMATED_HEIGHT);
      }
   }, [open]);

   useEffect(() => {
      const handleOutside = (e) => {
         if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
      };
      document.addEventListener('mousedown', handleOutside);
      return () => document.removeEventListener('mousedown', handleOutside);
   }, []);

   const goToWorkspace = (studentId) => {
      if (onClose) onClose();
      navigate('/students/' + studentId + '/workspace/' + courseId + '?topic=' + lt.topic.id);
   };

   const handleClick = () => {
      if (!courseId || participants.length === 0) return;
      if (participants.length === 1) {
         goToWorkspace(participants[0].student.id);
         return;
      }
      Promise.all(participants.map(p => (
         axios.get('/api/studentcourses?student=' + p.student.id, { withCredentials: true })
            .then(response => ({
               student: p.student,
               hasCourse: (Array.isArray(response.data) ? response.data : []).some(sc => sc.course.id === courseId),
            }))
            .catch(() => ({ student: p.student, hasCourse: false }))
      ))).then(results => {
         const filtered = results.filter(r => r.hasCourse).map(r => r.student);
         if (filtered.length === 1) {
            goToWorkspace(filtered[0].id);
            return;
         }
         setCandidates(filtered);
         setOpen(true);
      });
   };

   return (
      <span className='lesson-topic-pill' key={lt.id} title={lt.topic.name} ref={wrapRef}>
         <span>Тема: </span>
         <span className='lesson-topic-pill-label' onClick={handleClick}>{lt.topic.name}</span>
         <span className='lesson-topic-pill-remove' onClick={(e) => { e.stopPropagation(); lt.onRemove(lt.id); }}>×</span>
         {open ? (
            <div className={'lesson-topic-flyout' + (openDown ? ' open-down' : '')} onMouseDown={e => e.stopPropagation()}>
               <p className='lesson-topic-flyout-title'>{lang.lessonTopicsPickStudent}</p>
               {candidates === null ? (
                  <p className='lesson-topic-flyout-empty'>{lang['Loading ...']}</p>
               ) : candidates.length === 0 ? (
                  <p className='lesson-topic-flyout-empty'>{lang.lessonTopicsNoStudentHasCourse}</p>
               ) : (
                  <div className='lesson-topic-option-list'>
                     {candidates.map(s => (
                        <div key={s.id} className='lesson-topic-option' onClick={() => goToWorkspace(s.id)}>
                           {s.name} {s.surname}
                        </div>
                     ))}
                  </div>
               )}
            </div>
         ) : ''}
      </span>
   );
}

// Linked-topic pills + icon trigger for LessonDetailModal's header, right
// after the title. Pills are ALWAYS visible (fetched regardless of open
// state or participant count) so which topic(s) this lesson covers reads
// at a glance, no click needed - the flyout (inline, not a second modal -
// same interaction language as conversations-creator-canvas's
// WordLinkPickerPanel) only handles ADDING a new one.
//
// Adding needs at least one participant to know whose courses to offer.
// With exactly one, it's that student's own courses, same as always. With
// several (a group lesson), the courses offered are only ones ALL of them
// share - picking one adds a link every one of them can reach; there is no
// per-participant bookkeeping on the link itself (LessonTopic has no
// student field), the pill's own click-through handles "whose workspace"
// later, dynamically, against whoever is on the lesson at that time. With
// zero participants there's nobody to resolve courses for at all.
// groupId (present when this lesson belongs to a group) restricts the
// "add topic" course choice to that group's OWN assigned courses
// (GET /api/groups/:id/courses, the same source of truth GroupCourses.jsx
// itself uses) instead of the per-participant "courses every current
// participant happens to share" intersection below - the two can silently
// diverge (e.g. a personal course two members happen to both have, unrelated
// to the group's actual course), which would let a topic from a course the
// group was never assigned get linked to a group lesson.
export default function LessonTopicFlyout({ lessonId, participants, groupId, onClose }) {
   const [open, setOpen] = useState(false);
   const [openDown, setOpenDown] = useState(false);
   const [linkedTopics, setLinkedTopics] = useState([]);
   const [courses, setCourses] = useState(null);
   const [pickedCourseId, setPickedCourseId] = useState(null);
   const [topics, setTopics] = useState(null);
   const [query, setQuery] = useState('');
   const wrapRef = useRef(null);
   const lang = language[language.lang];

   const loadLinkedTopics = () => {
      getLessonTopics(lessonId)
         .then(response => setLinkedTopics(Array.isArray(response.data) ? response.data : []))
         .catch(() => setLinkedTopics([]));
   };

   useEffect(() => {
      loadLinkedTopics();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [lessonId]);

   useEffect(() => {
      if (!open) return;
      if (!groupId && participants.length === 0) return;
      setCourses(null);
      setPickedCourseId(null);
      setTopics(null);
      setQuery('');
      if (groupId) {
         axios.get('/api/groups/' + groupId + '/courses', { withCredentials: true })
            .then(response => {
               // normalized into the same {course:{...}} shape the per-
               // participant path below produces (that endpoint returns
               // StudentCourse rows, this one plain Course rows), so every
               // downstream sc.course.* read stays unchanged either way.
               const list = (Array.isArray(response.data) ? response.data : []).map(course => ({ course }));
               setCourses(list);
               if (list.length === 1) setPickedCourseId(list[0].course.id);
            })
            .catch(() => setCourses([]));
         return;
      }
      Promise.all(participants.map(p => (
         axios.get('/api/studentcourses?student=' + p.student.id, { withCredentials: true })
            .then(response => Array.isArray(response.data) ? response.data : [])
            .catch(() => [])
      ))).then(lists => {
         const [first, ...rest] = lists;
         const shared = rest.length === 0 ? first : first.filter(sc => rest.every(list => list.some(x => x.course.id === sc.course.id)));
         setCourses(shared);
         if (shared.length === 1) setPickedCourseId(shared[0].course.id);
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [open, lessonId, groupId]);

   useEffect(() => {
      if (!pickedCourseId) { setTopics(null); return; }
      collectTopicsUnder('course', pickedCourseId)
         .then(list => setTopics(Array.isArray(list) ? list : []))
         .catch(() => setTopics([]));
   }, [pickedCourseId]);

   useEffect(() => {
      if (open && wrapRef.current) {
         setOpenDown(wrapRef.current.getBoundingClientRect().top < FLYOUT_ESTIMATED_HEIGHT);
      }
   }, [open]);

   useEffect(() => {
      const handleOutside = (e) => {
         if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
      };
      document.addEventListener('mousedown', handleOutside);
      return () => document.removeEventListener('mousedown', handleOutside);
   }, []);

   const handlePickTopic = (topic) => {
      createLessonTopic(lessonId, topic.id).then(() => {
         loadLinkedTopics();
         setQuery('');
      }).catch(() => {});
   };

   const handleRemove = (linkId) => {
      deleteLessonTopic(linkId).then(loadLinkedTopics).catch(() => {});
   };

   const linkedIds = new Set(linkedTopics.map(lt => lt.topic.id));
   const filteredTopics = (topics || []).filter(t => {
      if (linkedIds.has(t.id)) return false;
      if (!query.trim()) return true;
      return (t.name || '').toLowerCase().includes(query.trim().toLowerCase());
   });

   // Group mode has its own, always-known source of courses (the group's
   // own assignment) - it doesn't need any participant to be on the lesson
   // yet to know what to offer, unlike the per-participant-intersection path.
   const hasParticipants = !!groupId || participants.length > 0;
   const isMultiParticipant = !groupId && participants.length > 1;
   const noCoursesLabel = isMultiParticipant ? lang.lessonTopicsNoCommonCourse : lang.lessonTopicsNoCourses;

   return (
      <span className='lesson-topic-header-group'>
         {linkedTopics.map(lt => (
            <TopicPillLink key={lt.id} lt={{ ...lt, onRemove: handleRemove }} participants={participants} onClose={onClose} />
         ))}
         <span className='lesson-topic-trigger-wrap' ref={wrapRef}>
            {hasParticipants ? (
               <span className={'lesson-topic-trigger-btn' + (open ? ' active' : '')} onClick={() => setOpen(v => !v)} title={lang.lessonTopics}>
                  <IconAddLink style={{ fill: open ? 'var(--lighttext-color)' : 'var(--secondarytext-color)', width: 15, height: 15 }} />
               </span>
            ) : (
               <span className='lesson-topic-trigger-btn disabled' title={lang.lessonTopicsSingleParticipantOnly}>
                  <IconAddLink style={{ fill: 'var(--secondarytext-color)', width: 15, height: 15 }} />
               </span>
            )}
            {open && hasParticipants ? (
               <div className={'lesson-topic-flyout' + (openDown ? ' open-down' : '')} onMouseDown={e => e.stopPropagation()}>
                  <p className='lesson-topic-flyout-title'>{lang.lessonTopics}</p>
                  {courses === null ? (
                     <p className='lesson-topic-flyout-empty'>{lang['Loading ...']}</p>
                  ) : courses.length === 0 ? (
                     <p className='lesson-topic-flyout-empty'>{noCoursesLabel}</p>
                  ) : (
                     <>
                        {courses.length > 1 ? (
                           <div className='lesson-topic-course-tabs'>
                              {courses.map(sc => (
                                 <span key={sc.id} className={'lesson-topic-course-tab' + (pickedCourseId === sc.course.id ? ' active' : '')}
                                    onClick={() => setPickedCourseId(sc.course.id)}>
                                    {sc.course.name}
                                 </span>
                              ))}
                           </div>
                        ) : ''}
                        {pickedCourseId ? (
                           <>
                              <input className='lesson-topic-search-input' type='text' placeholder={lang.lessonTopicsAddPlaceholder}
                                 value={query} onChange={e => setQuery(e.target.value)} />
                              <div className='lesson-topic-option-list'>
                                 {topics === null ? (
                                    <p className='lesson-topic-flyout-empty'>{lang['Loading ...']}</p>
                                 ) : filteredTopics.length === 0 ? (
                                    <p className='lesson-topic-flyout-empty'>{lang['Empty list']}</p>
                                 ) : filteredTopics.map(t => (
                                    <div key={t.id} className='lesson-topic-option' onClick={() => handlePickTopic(t)}>
                                       {t.name}
                                    </div>
                                 ))}
                              </div>
                           </>
                        ) : ''}
                     </>
                  )}
               </div>
            ) : ''}
         </span>
      </span>
   );
}
